import styled from 'styled-components';
import { useCallback } from 'react';
import { pick } from 'lodash';
import BaseMobileLegsTable from '#/components/UnifiedRfqs/components/MobileLegsTable';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/view';
import { useGrfqBbo } from '@paradigm/features/src/unified-rfqs/ui/rfqs/bbo';
import { useQuoteBuilderModalState } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/modal-context';
import { useQuoteBuilderActions } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/form-context';
import { darkerBlackPearl } from '@paradigm/design-system/src/colors';
import { Grfq } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';
import MarkPrice from '#/components/UnifiedRfqs/components/MarkPrice';
import { ChangeLegPriceArgs } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/form-reducer';
import { useDebouncedTrackAction } from '@paradigm/features/src/analytics';

export function MobileLegsTable() {
  const { legs, side } = useQuoteBuilderView();
  const { legPricingEnabled } = useQuoteBuilderModalState();
  const { legsPrices } = useQuoteBuilderView();
  const { changeLegPrice } = useQuoteBuilderActions();
  const bbo = useGrfqBbo();
  const trackAction = useDebouncedTrackAction();
  const handleChangeLegPrice = useCallback(
    (params: ChangeLegPriceArgs) => {
      trackAction('change leg price', pick(params, 'index', 'side'));
      changeLegPrice(params);
    },
    [changeLegPrice, trackAction],
  );

  return (
    <MobileLegsTableContainer>
      <BaseMobileLegsTable
        legs={legs}
        changeLegPrice={handleChangeLegPrice}
        legPrices={legsPrices.legs}
        legPricingEnabled={legPricingEnabled}
        markPrices={bbo.data?.leg_prices.map((leg) => leg.mark_price) ?? []}
        title={
          side === 'BUY'
            ? 'Strategy Details (Buyer)'
            : 'Strategy Details (Seller)'
        }
      />
    </MobileLegsTableContainer>
  );
}

export function MobileGrfqDate({ grfq }: { grfq: Grfq }) {
  return (
    <MobileGrfqDateContainer>
      <div>{hourFormat(grfq.created)}</div>
      <div>{shortDateFormat(grfq.created)}</div>
    </MobileGrfqDateContainer>
  );
}

export function MobileMarkPrice() {
  return (
    <MobileMarkPriceContainer>
      <MarkPrice />
    </MobileMarkPriceContainer>
  );
}

export const MobileCardContainer = styled.div`
  margin: 1rem 0;
  border-radius: 3px;
  padding: 0.75rem 1rem;
  background-color: ${darkerBlackPearl};
`;

export const MobileGrfqInfoContainer = styled.div`
  background-color: ${darkerBlackPearl};
  padding: 1rem;
  display: inline-grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  justify-content: space-between;

  * {
    margin-bottom: 0;
  }
`;

const MobileLegsTableContainer = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

const MobileGrfqDateContainer = styled.div`
  font-size: 0.75rem;
  opacity: 0.7;
`;

const MobileMarkPriceContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding-top: 0.6rem;
  padding-bottom: 1rem;
  margin: 0 auto;

  .mark-row-container {
    grid-template-columns: 2rem auto;
  }

  span {
    font-size: 0.875rem;
    line-height: 1rem;
  }
`;
